import { useEffect, useState } from "react";

const CONSENT_STORAGE_KEY = "y2.privacy-consent.v2";
const LEGACY_CONSENT_STORAGE_KEY = "y2.privacy-consent.v1";
const CONSENT_VERSION = 2;

let volatileConsent: { owner: Window; value: PrivacyConsent } | undefined;

export const PRIVACY_CONSENT_CHANGED_EVENT = "y2:privacy-consent-changed";
export const PRIVACY_PREFERENCES_OPEN_EVENT = "y2:privacy-preferences-open";

export type PrivacyConsent = {
  version: number;
  analytics: boolean;
  referral: boolean;
  marketing: boolean;
  updatedAt: string;
};

function hasGlobalPrivacyControl(): boolean {
  if (typeof navigator === "undefined") return false;
  return (
    (navigator as Navigator & { globalPrivacyControl?: boolean })
      .globalPrivacyControl === true
  );
}

function parseConsent(value: string | null): PrivacyConsent | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as Partial<PrivacyConsent>;
    if (
      parsed.version !== CONSENT_VERSION ||
      typeof parsed.analytics !== "boolean" ||
      typeof parsed.referral !== "boolean" ||
      typeof parsed.marketing !== "boolean" ||
      typeof parsed.updatedAt !== "string"
    ) {
      return null;
    }
    return {
      version: CONSENT_VERSION,
      analytics: parsed.analytics,
      referral: parsed.referral,
      marketing: hasGlobalPrivacyControl() ? false : parsed.marketing,
      updatedAt: parsed.updatedAt,
    };
  } catch {
    return null;
  }
}

export function readPrivacyConsent(): PrivacyConsent | null {
  if (typeof window === "undefined") return null;
  if (volatileConsent?.owner === window) return volatileConsent.value;
  try {
    return parseConsent(window.localStorage.getItem(CONSENT_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function savePrivacyConsent(selection: {
  analytics: boolean;
  referral: boolean;
  marketing: boolean;
}): PrivacyConsent {
  const consent: PrivacyConsent = {
    version: CONSENT_VERSION,
    analytics: selection.analytics,
    referral: selection.referral,
    marketing: hasGlobalPrivacyControl() ? false : selection.marketing,
    updatedAt: new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));
    volatileConsent = undefined;
    window.localStorage.removeItem(LEGACY_CONSENT_STORAGE_KEY);
  } catch {
    // Keep an explicit choice usable for this page when browser storage is blocked.
    volatileConsent = { owner: window, value: consent };
  }
  window.dispatchEvent(
    new CustomEvent(PRIVACY_CONSENT_CHANGED_EVENT, { detail: consent }),
  );
  return consent;
}

export function openPrivacyPreferences(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(PRIVACY_PREFERENCES_OPEN_EVENT));
}

export function isGlobalPrivacyControlEnabled(): boolean {
  return hasGlobalPrivacyControl();
}

export function usePrivacyConsent(): PrivacyConsent | null {
  const [consent, setConsent] = useState<PrivacyConsent | null>(() =>
    readPrivacyConsent(),
  );

  useEffect(() => {
    const handleChange = (event: Event) => {
      const nextConsent = (event as CustomEvent<PrivacyConsent>).detail;
      setConsent(nextConsent ?? readPrivacyConsent());
    };
    window.addEventListener(PRIVACY_CONSENT_CHANGED_EVENT, handleChange);
    return () =>
      window.removeEventListener(PRIVACY_CONSENT_CHANGED_EVENT, handleChange);
  }, []);

  return consent;
}

export function usePrivacyConsentCategory(
  category: "analytics" | "referral" | "marketing",
): boolean {
  return usePrivacyConsent()?.[category] === true;
}
